import React, { useEffect, useState } from 'react';
import { Phone, CheckCircle, Calendar, ChatCircle, Clock, FileText } from '@phosphor-icons/react';
import { usePersona, personas } from './PersonaContext';
type Mode = 'book' | 'message' | 'check';
const otherOptions = ['Bookkeeping and BAS only', 'Payroll only', 'Not sure yet, just exploring'];
export function LeadCapture() {
  const {
    persona
  } = usePersona();
  const [mode, setMode] = useState<Mode>('book');
  const [topic, setTopic] = useState(persona ? personas[persona].formPrefill : '');
  const [message, setMessage] = useState('');
  useEffect(() => {
    if (persona) {
      setTopic(personas[persona].formPrefill);
      setMessage(`I am a ${personas[persona].shortLabel}. I am looking for ${personas[persona].formPrefill.toLowerCase()}.`);
    }
  }, [persona]);
  const modes: Array<{
    id: Mode;
    label: string;
    icon: typeof Calendar;
  }> = [{
    id: 'book',
    label: 'Book a consultation',
    icon: Calendar
  }, {
    id: 'check',
    label: 'Free back-office health check',
    icon: FileText
  }, {
    id: 'message',
    label: 'Quick message',
    icon: ChatCircle
  }];
  const personaOptions = Object.values(personas).map(p => p.formPrefill);
  const allOptions = [...personaOptions, ...otherOptions];
  return <section id="lead" className="py-24 lg:py-28 bg-near-black relative overflow-hidden" data-id="element-207">
      <div className="absolute inset-0 bg-grid-pattern opacity-20 pointer-events-none" data-id="element-208"></div>
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-knight-orange/5 rounded-full blur-3xl pointer-events-none" data-id="element-209"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-210">
        <div className="text-center max-w-3xl mx-auto mb-12" data-id="element-211">
          <p className="font-mono text-[11px] tracking-wider uppercase text-knight-orange mb-3" data-id="element-212">
            Start with a conversation
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-white leading-tight mb-4" data-id="element-213">
            Find out what bookkeeping looks like with CFO thinking built in.
          </h2>
          <p className="text-lg text-gray-400 leading-relaxed" data-id="element-214">
            {persona ? <>
                You selected{' '}
                <span className="text-knight-orange font-semibold" data-id="element-215">
                  {personas[persona].shortLabel}
                </span>
                . We have pre-filled the form. Confirm your details and we will
                come back within 4 business hours.
              </> : <>
                Book a free, no-obligation consultation. Tell us where the
                pressure is, and we will show you exactly how Knight would
                handle it.
              </>}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-5 lg:gap-6 max-w-6xl mx-auto" data-id="element-216">
          <div className="lg:col-span-3" data-id="element-217">
            <div className="bg-white rounded-md overflow-hidden border border-hairline" data-id="element-218">
              <div className="grid grid-cols-3 border-b border-hairline" data-id="element-219">
                {modes.map(m => {
                const Icon = m.icon;
                return <button key={m.id} onClick={() => setMode(m.id)} className={`px-3 py-4 text-xs sm:text-sm font-semibold transition-colors duration-300 ease-out-expo flex items-center justify-center gap-1.5 ${mode === m.id ? 'bg-white text-knight-orange border-b-2 border-knight-orange' : 'bg-off-white text-gray-500 hover:text-charcoal'}`} data-id="element-220">
                      <Icon size={16} weight="regular" data-id="element-221" />
                      <span className="hidden sm:inline" data-id="element-222">{m.label}</span>
                      <span className="sm:hidden" data-id="element-223">{m.label.split(' ')[0]}</span>
                    </button>;
              })}
              </div>

              <div className="p-6 sm:p-8" data-id="element-224">
                {mode === 'book' && <form className="space-y-4" onSubmit={e => e.preventDefault()} data-id="element-225">
                    <p className="text-sm text-gray-500 mb-2" data-id="element-226">
                      Three quick details. We will come back within 4 business
                      hours to confirm a 30-minute call that suits you.
                    </p>
                    <div data-id="element-227">
                      <label className="block text-sm font-semibold text-charcoal mb-1.5" data-id="element-228">
                        Your name
                      </label>
                      <input type="text" required className="w-full border border-hairline rounded-md px-4 py-3 focus:ring-2 focus:ring-knight-orange focus:border-knight-orange outline-none transition-all" data-id="element-229" />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-230">
                      <div data-id="element-231">
                        <label className="block text-sm font-semibold text-charcoal mb-1.5" data-id="element-232">
                          Email
                        </label>
                        <input type="email" required className="w-full border border-hairline rounded-md px-4 py-3 focus:ring-2 focus:ring-knight-orange focus:border-knight-orange outline-none transition-all" data-id="element-233" />
                      </div>
                      <div data-id="element-234">
                        <label className="block text-sm font-semibold text-charcoal mb-1.5" data-id="element-235">
                          Phone
                        </label>
                        <input type="tel" required className="w-full border border-hairline rounded-md px-4 py-3 focus:ring-2 focus:ring-knight-orange focus:border-knight-orange outline-none transition-all" data-id="element-236" />
                      </div>
                    </div>
                    <div data-id="element-237">
                      <label className="block text-sm font-semibold text-charcoal mb-1.5" data-id="element-238">
                        What are you looking for?{' '}
                        {persona && <span className="font-mono text-[10px] text-knight-orange uppercase tracking-wider ml-1" data-id="element-239">
                            / pre-filled
                          </span>}
                      </label>
                      <select value={topic} onChange={e => setTopic(e.target.value)} className="w-full border border-hairline rounded-md px-4 py-3 focus:ring-2 focus:ring-knight-orange focus:border-knight-orange outline-none transition-all bg-white" data-id="element-240">
                        <option value="" data-id="element-241">
                          Select what you would like to discuss
                        </option>
                        {allOptions.map(opt => <option key={opt} value={opt} data-id="element-242">
                            {opt}
                          </option>)}
                      </select>
                    </div>
                    <button type="submit" className="w-full bg-knight-orange text-white font-bold text-base py-4 rounded-md hover:bg-knight-orange-hover transition-colors duration-300 ease-out-expo mt-2" data-id="element-243">
                      Request My Consultation
                    </button>
                    <div className="flex items-center justify-center gap-2 font-mono text-[11px] text-gray-500 pt-2 uppercase tracking-wide" data-id="element-244">
                      <Clock size={12} weight="regular" data-id="element-245" />
                      Reply in {'<'} 4 hours / No startup costs / No lock-in
                    </div>
                  </form>}

                {mode === 'check' && <form className="space-y-4" onSubmit={e => e.preventDefault()} data-id="element-246">
                    <div className="bg-off-white border border-hairline rounded-md p-4 mb-2 flex items-start gap-3" data-id="element-247">
                      <FileText size={20} weight="duotone" className="text-knight-orange flex-shrink-0 mt-0.5" data-id="element-248" />
                      <div data-id="element-249">
                        <p className="text-sm font-semibold text-charcoal mb-1" data-id="element-250">
                          Free back-office health check
                        </p>
                        <p className="text-xs text-gray-600 leading-relaxed" data-id="element-251">
                          A 15-minute review of your current bookkeeping,
                          payroll and reporting setup. We tell you what is
                          working, what is not, and what we would change. Yours
                          to keep, no obligation.
                        </p>
                      </div>
                    </div>
                    <div data-id="element-252">
                      <label className="block text-sm font-semibold text-charcoal mb-1.5" data-id="element-253">
                        Your name
                      </label>
                      <input type="text" required className="w-full border border-hairline rounded-md px-4 py-3 focus:ring-2 focus:ring-knight-orange focus:border-knight-orange outline-none transition-all" data-id="element-254" />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-255">
                      <div data-id="element-256">
                        <label className="block text-sm font-semibold text-charcoal mb-1.5" data-id="element-257">
                          Email
                        </label>
                        <input type="email" required className="w-full border border-hairline rounded-md px-4 py-3 focus:ring-2 focus:ring-knight-orange focus:border-knight-orange outline-none transition-all" data-id="element-258" />
                      </div>
                      <div data-id="element-259">
                        <label className="block text-sm font-semibold text-charcoal mb-1.5" data-id="element-260">
                          Company
                        </label>
                        <input type="text" required className="w-full border border-hairline rounded-md px-4 py-3 focus:ring-2 focus:ring-knight-orange focus:border-knight-orange outline-none transition-all" data-id="element-261" />
                      </div>
                    </div>
                    <div data-id="element-262">
                      <label className="block text-sm font-semibold text-charcoal mb-1.5" data-id="element-263">
                        Focus area{' '}
                        {persona && <span className="font-mono text-[10px] text-knight-orange uppercase tracking-wider ml-1" data-id="element-264">
                            / pre-filled
                          </span>}
                      </label>
                      <select value={topic} onChange={e => setTopic(e.target.value)} className="w-full border border-hairline rounded-md px-4 py-3 focus:ring-2 focus:ring-knight-orange focus:border-knight-orange outline-none transition-all bg-white" data-id="element-265">
                        <option value="" data-id="element-266">Select a focus area</option>
                        {allOptions.map(opt => <option key={opt} value={opt} data-id="element-267">
                            {opt}
                          </option>)}
                      </select>
                    </div>
                    <button type="submit" className="w-full bg-knight-orange text-white font-bold text-base py-4 rounded-md hover:bg-knight-orange-hover transition-colors duration-300 ease-out-expo mt-2" data-id="element-268">
                      Request My Health Check
                    </button>
                    <div className="text-center font-mono text-[11px] text-gray-500 pt-2 uppercase tracking-wide" data-id="element-269">
                      Free / Confidential / No obligation
                    </div>
                  </form>}

                {mode === 'message' && <form className="space-y-4" onSubmit={e => e.preventDefault()} data-id="element-270">
                    <p className="text-sm text-gray-500 mb-2" data-id="element-271">
                      Send us a message. We will reply within 4 business hours.
                    </p>
                    <div data-id="element-272">
                      <label className="block text-sm font-semibold text-charcoal mb-1.5" data-id="element-273">
                        Your name
                      </label>
                      <input type="text" required className="w-full border border-hairline rounded-md px-4 py-3 focus:ring-2 focus:ring-knight-orange focus:border-knight-orange outline-none transition-all" data-id="element-274" />
                    </div>
                    <div data-id="element-275">
                      <label className="block text-sm font-semibold text-charcoal mb-1.5" data-id="element-276">
                        Email
                      </label>
                      <input type="email" required className="w-full border border-hairline rounded-md px-4 py-3 focus:ring-2 focus:ring-knight-orange focus:border-knight-orange outline-none transition-all" data-id="element-277" />
                    </div>
                    <div data-id="element-278">
                      <label className="block text-sm font-semibold text-charcoal mb-1.5" data-id="element-279">
                        What can we help with?{' '}
                        {persona && <span className="font-mono text-[10px] text-knight-orange uppercase tracking-wider ml-1" data-id="element-280">
                            / pre-filled from your selection
                          </span>}
                      </label>
                      <textarea rows={4} required value={message} onChange={e => setMessage(e.target.value)} className="w-full border border-hairline rounded-md px-4 py-3 focus:ring-2 focus:ring-knight-orange focus:border-knight-orange outline-none transition-all resize-none" data-id="element-281"></textarea>
                    </div>
                    <button type="submit" className="w-full bg-knight-orange text-white font-bold text-base py-4 rounded-md hover:bg-knight-orange-hover transition-colors duration-300 ease-out-expo mt-2" data-id="element-282">
                      Send Message
                    </button>
                    <div className="text-center font-mono text-[11px] text-gray-500 pt-2 uppercase tracking-wide" data-id="element-283">
                      Confidential / No obligation
                    </div>
                  </form>}
              </div>
            </div>
          </div>

          <div className="lg:col-span-2" data-id="element-284">
            <div className="bg-[#161616] rounded-md p-8 border border-hairline-dark h-full flex flex-col" data-id="element-285">
              <div className="inline-flex items-center px-2.5 py-1 rounded bg-knight-orange/10 font-mono text-[10px] font-semibold text-knight-orange uppercase tracking-wider mb-4 self-start" data-id="element-286">
                Prefer to speak now?
              </div>

              <a href="tel:+61863179998" className="block mb-8 group" data-id="element-287">
                <div className="flex items-center text-knight-orange mb-1" data-id="element-288">
                  <Phone size={28} weight="regular" className="mr-3 transition-transform group-hover:scale-110 duration-300 ease-out-expo" data-id="element-289" />
                  <span className="font-mono text-2xl sm:text-3xl font-semibold tracking-tight" data-id="element-290">
                    +61 8 6317 9998
                  </span>
                </div>
                <p className="font-mono text-[11px] text-gray-500 pl-10 uppercase tracking-wider" data-id="element-291">
                  Mon to Fri / AWST
                </p>
              </a>

              <div className="space-y-6 mb-8 flex-grow" data-id="element-292">
                <div data-id="element-293">
                  <h4 className="font-mono text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2" data-id="element-294">
                    Perth Office
                  </h4>
                  <p className="text-sm text-gray-200 leading-relaxed" data-id="element-295">
                    Level 2 East, The Wentworth Building
                    <br data-id="element-296" />
                    300 Murray Street
                    <br data-id="element-297" />
                    Perth WA 6000
                  </p>
                </div>
                <div data-id="element-298">
                  <h4 className="font-mono text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2" data-id="element-299">
                    Sydney Office
                  </h4>
                  <p className="text-sm text-gray-200 leading-relaxed" data-id="element-300">
                    By appointment
                    <br data-id="element-301" />
                    Sydney CBD, NSW
                  </p>
                </div>
                <div data-id="element-302">
                  <h4 className="font-mono text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2" data-id="element-303">
                    Postal Address
                  </h4>
                  <p className="text-sm text-gray-200 leading-relaxed" data-id="element-304">
                    PO Box 7524
                    <br data-id="element-305" />
                    Cloisters Square PO WA 6850
                  </p>
                </div>
              </div>

              <div className="space-y-2.5 pt-6 border-t border-hairline-dark" data-id="element-306">
                {['IPA-recognised supervision', 'ACSC Essential Eight-aligned', 'Fixed monthly fees', 'No lock-in contracts'].map((pill, i) => <div key={i} className="flex items-center text-sm text-gray-300" data-id="element-307">
                    <CheckCircle size={16} weight="regular" className="text-knight-orange mr-2.5 flex-shrink-0" data-id="element-308" />
                    {pill}
                  </div>)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}